// School-admin → Classes. Create a class (name + grade + optional teacher),
// list classes with teacher + roster size, delete. docs/replan-v2.md R9.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Badge, Button, Card, CardBody, Field, Input, Skeleton } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError } from '~/lib/api';
import {
  createClass,
  deleteClass,
  listClasses,
  listTeachers,
  type Grade,
  type SchoolClass,
} from '~/lib/school';

const GRADES: Grade[] = ['kg2', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6'];

export const Route = createFileRoute('/$locale/school/classes')({
  component: ClassesScreen,
});

function ClassesScreen() {
  const t = useT();
  const qc = useQueryClient();
  const classes = useQuery({
    queryKey: ['school', 'classes'],
    queryFn: ({ signal }) => listClasses(signal),
  });
  const teachers = useQuery({
    queryKey: ['school', 'teachers'],
    queryFn: ({ signal }) => listTeachers(signal),
  });

  const [name, setName] = useState('');
  const [grade, setGrade] = useState<Grade>('kg2');
  const [teacherUserId, setTeacherUserId] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createClass({ name: name.trim(), grade, teacherUserId: teacherUserId || null }),
    onSuccess: async () => {
      setName('');
      setTeacherUserId('');
      await qc.invalidateQueries({ queryKey: ['school', 'classes'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteClass(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['school', 'classes'] });
    },
  });

  const activeTeachers = (teachers.data ?? []).filter((te) => te.status === 'active');

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.school.classes_title')}</h1>

      <Card className="mt-6">
        <CardBody>
          {create.isError && (
            <Alert tone="danger" className="mb-4">
              {create.error instanceof ApiError
                ? create.error.message
                : t('app.common.error_generic')}
            </Alert>
          )}
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label={t('app.school.class_name')}>
              {(id) => (
                <Input id={id} value={name} onChange={(e) => setName(e.target.value)} required />
              )}
            </Field>
            <Field label={t('app.school.grade')}>
              {(id) => (
                <select
                  id={id}
                  value={grade}
                  onChange={(e) => setGrade(e.target.value as Grade)}
                  className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                >
                  {GRADES.map((g) => (
                    <option key={g} value={g}>
                      {g.toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('app.school.teacher')}>
              {(id) => (
                <select
                  id={id}
                  value={teacherUserId}
                  onChange={(e) => setTeacherUserId(e.target.value)}
                  className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                >
                  <option value="">{t('app.school.unassigned')}</option>
                  {activeTeachers.map((te) => (
                    <option key={te.id} value={te.id}>
                      {te.displayName ?? te.email}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <div className="flex items-end">
              <Button type="submit" disabled={create.isPending}>
                {t('app.school.create')}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <div className="mt-8 space-y-3">
        {classes.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (classes.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-ink-700">{t('app.school.no_classes')}</p>
        ) : (
          classes.data!.map((cls) => (
            <ClassRow key={cls.id} cls={cls} onRemove={() => remove.mutate(cls.id)} />
          ))
        )}
      </div>
    </div>
  );
}

function ClassRow({ cls, onRemove }: { cls: SchoolClass; onRemove: () => void }) {
  const t = useT();
  return (
    <div className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-[0_12px_30px_-22px_rgba(31,26,23,0.25)] ring-1 ring-burgundy-500/10">
      <div>
        <p className="font-semibold text-ink-900">{cls.name}</p>
        <p className="mt-0.5 text-sm text-ink-500">
          <span className="uppercase">{cls.grade}</span> ·{' '}
          {cls.teacher?.displayName ?? t('app.school.unassigned')}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Badge tone="brand">{t('app.school.student_count', { n: cls.studentCount })}</Badge>
        <Button variant="ghost" size="sm" onClick={onRemove}>
          {t('app.school.remove')}
        </Button>
      </div>
    </div>
  );
}
